/**
 * useNotificationPopups.ts
 *
 * Mirrors the legacy `checkForNotifications()` loop in
 * `html/assets-ftp/js/nav.js` (around lines 265-318): polls every 5 s
 * while the tab is visible, and pops bottom-right toasts for each new
 * unread notification.
 *
 * Differences from legacy (intentional, "modern" read semantics per the
 * project plan):
 *   - The server endpoint does NOT mark anything as read. The hook calls
 *     `PATCH /notifications/{id}/read` when the user clicks the pop-up's body
 *     or its close button. A pop-up that expires on its own stays unread, so
 *     it keeps counting in the sidebar and the Dashboard Alerts card.
 *   - Already-popped notification IDs are tracked client-side in
 *     localStorage so they don't re-pop on page refresh while still
 *     unread.
 *
 * It also keeps the unread-alert queries (`['notifications']`) fresh: it is
 * where the app first learns of a new alert, so it refreshes them when a check
 * surfaces one, and after a click or close has marked one read.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/store/toastStore';
import {
  getPopNotifications,
  markRead,
  type PopNotification,
} from '@/services/notifications.service';
import { runNotificationAction } from '@/lib/notification-display';
import type { ToastNotificationGroup } from '@/types/common.types';
import { NOTIFICATIONS_KEY } from '@/hooks/useUnreadAlertCount';

const POLL_INTERVAL_MS = 5000;
const SHOWN_PRUNE_MS = 1000 * 60 * 60; // 1 hour
const SHOWN_KEY_PREFIX = 'ftp-notification-popups-shown-';

interface ShownEntry {
  id: number;
  t: number;
}

function loadShown(userId: number): Map<number, number> {
  try {
    const raw = localStorage.getItem(SHOWN_KEY_PREFIX + userId);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as ShownEntry[];
    const cutoff = Date.now() - SHOWN_PRUNE_MS;
    return new Map(arr.filter((e) => e.t >= cutoff).map((e) => [e.id, e.t]));
  } catch {
    return new Map();
  }
}

function saveShown(userId: number, shown: Map<number, number>): void {
  try {
    const arr: ShownEntry[] = Array.from(shown.entries()).map(([id, t]) => ({ id, t }));
    localStorage.setItem(SHOWN_KEY_PREFIX + userId, JSON.stringify(arr));
  } catch {
    // Storage full / disabled — ignore.
  }
}

const KNOWN_GROUPS = new Set<ToastNotificationGroup>([
  'lead', 'email', 'sms', 'message', 'payment',
  'appt', 'clinical', 'note', 'eform', 'report', 'unknown',
]);

function coerceGroup(group: string): ToastNotificationGroup {
  return (KNOWN_GROUPS.has(group as ToastNotificationGroup) ? group : 'unknown') as ToastNotificationGroup;
}

export function useNotificationPopups(): void {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id ?? 0;
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!isAuthenticated || userId <= 0) return;

    let cancelled = false;
    const shown = loadShown(userId);

    const refreshAlertCounts = () => {
      void queryClientRef.current.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    };

    // Refresh only once the write has settled — a refetch that races it reads
    // the old count. A failed write still refreshes, so the count re-syncs.
    const markReadThenRefresh = (id: number) => {
      markRead(id).catch(() => undefined).finally(refreshAlertCounts);
    };

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      try {
        const rows = await getPopNotifications();
        let touched = false;
        for (const row of rows) {
          if (shown.has(row.notificationId)) continue;

          shown.set(row.notificationId, Date.now());
          touched = true;

          enqueuePopup(row, () => {
            // Mark-read happens lazily on click so an ignored popup still
            // sits in the Alerts list as unread (modern UX choice). Swallow
            // errors — the user dismisses the toast either way.
            markReadThenRefresh(row.notificationId);

            // Per-typeID dispatch into the active modal (lead/payment/
            // appointment/note/survey) or a plain navigate. See
            // `notificationAction` in lib/notification-display.ts.
            const dispatched = runNotificationAction(row, navigateRef.current);
            if (!dispatched && row.display.link) {
              navigateRef.current(row.display.link);
            }
          }, () => markReadThenRefresh(row.notificationId));
        }
        if (touched) {
          saveShown(userId, shown);
          refreshAlertCounts();
        }
      } catch {
        // Network blip — silently skip this tick.
      }
    };

    void tick();
    const intervalId = window.setInterval(() => { void tick(); }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, userId]);
}

function enqueuePopup(row: PopNotification, onClick: () => void, onClose: () => void): void {
  toast.custom({
    variant: 'notification',
    title: row.display.title,
    description: row.display.body,
    notificationId: row.notificationId,
    notificationGroup: coerceGroup(row.display.group),
    onClick,
    // The close button is a deliberate "seen": it marks the alert read.
    onClose,
  });
}
